import { z } from 'zod';
import { RowVersionSchema } from '../common/row-version.js';
import { paginatedResponseSchema } from '../common/pagination.js';
import { UserLifecycleStatusSchema } from '../common/userStatus.js';
const Id16 = z.string().max(16);
// ── the list row ─────────────────────────────────────────────────────────────
export const TeamListItemSchema = z
    .object({
    row_version: RowVersionSchema,
    id: Id16,
    name: z.string(),
    is_disabled: z.boolean(),
    /** null = the project's time zone (ADR-082 §2). */
    timezone_iana: z.string().nullable(),
    member_count: z.number().int().nonnegative(),
    /** The members who can be assigned — what the assignee picker and the rota offer. */
    assignable_count: z.number().int().nonnegative(),
    /** The viewer is a member (the Mine chip's fact; the default order's first key). */
    mine: z.boolean(),
    created_at: z.string(),
})
    .strict();
/** The strip's truthful counts (ADR-078 §2) over the viewer's whole population: the viewer's own
 *  teams, and the disabled ones. */
export const TeamCountsSchema = z.object({ mine: z.number().int().nonnegative(), disabled: z.number().int().nonnegative() }).strict();
export const TeamsListResponseSchema = paginatedResponseSchema(TeamListItemSchema).extend({ counts: TeamCountsSchema }).strict();
// ── the small list (a picker's) ──────────────────────────────────────────────
export const TeamNameRowSchema = z.object({ id: Id16, name: z.string(), is_disabled: z.boolean() }).strict();
export const TeamNamesResponseSchema = z.object({ items: z.array(TeamNameRowSchema) }).strict();
// ── the team page ────────────────────────────────────────────────────────────
/** A member as the team page lists them: the identity (the person's face — the name, the project
 *  ROLE read from the membership, the account status), whether they can be assigned, whether
 *  this project's bot can reach them (yes / no — the handle and the connect invite stay the people
 *  key's, ADR-083 §5), and whether the VIEWER may open the person (`can_open` — the people key
 *  under its managed-roles bound; the name is a reference only when true, ADR-036 §6). */
export const TeamMemberSchema = z
    .object({
    user_id: Id16,
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    username: z.string(),
    /** The person's project role; null for a project administrator. */
    role_name: z.string().nullable(),
    status: UserLifecycleStatusSchema,
    can_be_assigned: z.boolean(),
    can_open: z.boolean(),
    bot_reachable: z.boolean(),
    /** When they joined the team (the row's birth; the trail holds the earlier stints). */
    joined_at: z.string(),
})
    .strict();
/** Where the team works — the places by kind, named; the whole project is the `whole_project` flag. Zones and
 *  clusters are vocabulary (named for every member — ADR-026 §A); a site, a depot or a pool is named only inside
 *  the VIEWER's own reach, the rest COUNTED in `outside` (ADR-066 §9 — the name never travels). */
const namedPlace = z.object({ id: Id16, name: z.string() }).strict();
export const TeamWorkAreaSchema = z
    .object({
    whole_project: z.boolean(),
    zones: z.array(z.object({ id: z.number().int(), name: z.string() }).strict()),
    clusters: z.array(z.object({ id: z.number().int(), name: z.string(), zone_id: z.number().int() }).strict()),
    sites: z.array(z.object({ id: Id16, name: z.string(), reference: z.string().nullable() }).strict()),
    warehouses: z.array(namedPlace),
    vehicle_pools: z.array(namedPlace),
    outside: z.object({ sites: z.number().int(), warehouses: z.number().int(), vehicle_pools: z.number().int() }).strict(),
})
    .strict();
export const TeamDetailSchema = z
    .object({
    row_version: RowVersionSchema,
    id: Id16,
    name: z.string(),
    is_disabled: z.boolean(),
    timezone_iana: z.string().nullable(),
    created_at: z.string(),
    created_by: z.object({ id: Id16, label: z.string() }).strict().nullable(),
    /** The viewer is a member. */
    mine: z.boolean(),
    /** The viewer may run the team (`team:manage` × team scope) — the page's write affordances. */
    can_manage: z.boolean(),
    /** The viewer may name the team's chains — the people key, the team level in their level-set, the team in their team scope (the launcher's gate). */
    can_assign: z.boolean(),
    members: z.array(TeamMemberSchema),
    work_area: TeamWorkAreaSchema,
})
    .strict();
/** A write that returns the row's summary (create · update · disable · reactivate). */
export const TeamSchema = TeamDetailSchema.omit({ members: true, work_area: true, mine: true, can_manage: true, can_assign: true });
/** The members' summary a membership write answers with. */
export const TeamMembersResponseSchema = z.object({ items: z.array(TeamMemberSchema) }).strict();
